/**
 * Utility functions for persisting analytics filter state per sub-page
 */

export interface AnalyticsFilterState {
  selectedAthletes?: string[];
  startDate?: string;
  endDate?: string;
  selectedLocation?: string;
  selectedHillSize?: string;
  selectedSessionTypes?: string[];
  selectedGate?: string;
  selectedPeriod?: 'daily' | 'weekly' | 'monthly';
  selectedEquipment?: string[]; // Legacy support
  selectedEquipmentByTab?: Record<string, string[]>; // Per-tab equipment selection
  selectedCategory?: string;
  selectedTrainingType?: string;
  selectedTrainingTypes?: string[];
  activeTab?: string;
}

const STORAGE_PREFIX = 'analytics_filters_';

/**
 * Get storage key for a specific analytics page
 */
const getStorageKey = (pagePath: string): string => {
  return `${STORAGE_PREFIX}${pagePath}`;
};

/**
 * Save filter state for a specific analytics page
 */
export const saveAnalyticsFilters = (pagePath: string, filters: AnalyticsFilterState): void => {
  try {
    const key = getStorageKey(pagePath);
    localStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving analytics filters:', error);
  }
};

/**
 * Load filter state for a specific analytics page
 */
export const loadAnalyticsFilters = (pagePath: string): AnalyticsFilterState | null => {
  try {
    const key = getStorageKey(pagePath);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as AnalyticsFilterState;
    }
  } catch (error) {
    console.error('Error loading analytics filters:', error);
  }
  return null;
};

/**
 * Clear filter state for a specific analytics page
 */
export const clearAnalyticsFilters = (pagePath: string): void => {
  try {
    const key = getStorageKey(pagePath);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing analytics filters:', error);
  }
};

/**
 * Get page path from current route or tab query parameter
 * Maps route paths or tab query params to storage keys
 */
export const getPagePathFromRoute = (pathname: string, searchParams?: URLSearchParams): string => {
  // First check for tab query parameter (new tab-based system)
  if (searchParams) {
    const tab = searchParams.get('tab');
    if (tab) {
      return tab;
    }
  }
  
  // Fallback to old route-based system (for backward compatibility during migration)
  // Extract the analytics sub-page path
  // e.g., '/coach/analytics/distance' -> 'distance'
  // e.g., '/coach/analytics/gate-number' -> 'gate-number'
  const match = pathname.match(/\/analytics\/(.+)$/);
  if (match && match[1]) {
    return match[1];
  }
  return '';
};

